import type { PortfolioSnapshot } from '../types';
import type { DisplayRateTable } from './displayRates';

/**
 * A USD portfolio-value series, converted into the display currency for charting.
 *
 * ── Which rate, per point ────────────────────────────────────────────────────
 *
 * Every point except today's is what the portfolio was worth ON THAT DAY, so it
 * is converted at that day's rate (RBA, then ECB, via the display rate table),
 * the same way the CGT engine converts a dated amount. Converting a year-old
 * value at today's rate would just rescale the USD line, and for a non-USD
 * investor the currency's own movement is part of how their portfolio's value
 * actually moved.
 *
 * Today's point is different: it is REPLACED by the live total, converted at
 * today's spot exactly as the Total Value card converts it. The two figures sit
 * side by side on the dashboard, so the line must end on the card's number to
 * the cent. Recomputing it from the series' own today value, or from the table's
 * published rate for today (which is not the live spot), would differ slightly
 * and look like a bug.
 */
/** Anything that can say how many display-currency units one USD was on a date. */
export type PerUsdOn = Pick<DisplayRateTable, 'perUsdOn'>;

/**
 * Per-date rates for charting a USD series: that day's published rate for past
 * days, and today's live spot for today (and anything later).
 *
 * Why spot for today rather than the table's answer: the table may hold a
 * published rate for today, which is not the spot the rest of the screen uses.
 * The newest point of a price chart sits next to a header price converted at
 * spot, so it must use the same number.
 */
export function chartRates(table: PerUsdOn, spotPerUsd: number, todayUtc: string): PerUsdOn {
  return {
    perUsdOn: (date: string) => (date >= todayUtc ? spotPerUsd : table.perUsdOn(date)),
  };
}

export interface LiveTotal {
  /** The series' notion of today: a UTC ISO date, as the series dates are. */
  date: string;
  /** The Total Value card's figure, already in the display currency. */
  valueDisplay: number;
}

export function toDisplaySeries(
  usdSeries: readonly PortfolioSnapshot[],
  rates: PerUsdOn,
  live: LiveTotal | null
): PortfolioSnapshot[] {
  const out = usdSeries.map((p) => ({ date: p.date, value: p.value * rates.perUsdOn(p.date) }));

  // Nothing to end on. An empty series is the chart's loading / empty state, and
  // inventing a one-point line here would skip it.
  if (!live || out.length === 0) return out;

  const last = out[out.length - 1];
  if (last.date < live.date) {
    // A snapshot series whose newest entry predates today (the app was not opened
    // yesterday, or prices have not loaded yet today): extend it to today.
    out.push({ date: live.date, value: live.valueDisplay });
  } else {
    out[out.length - 1] = { date: last.date, value: live.valueDisplay };
  }
  return out;
}
